/**
 * RILA/VA filter-block helpers (added 2026-06-05).
 *
 * The six dimensions in the V2 RILA/VA filter block (Buffer Level, Floor
 * Level, Segment Duration, Rate Type, Min Premium Range, Protection Type)
 * are mostly straight reads of row fields, but two need derivation and
 * two need numeric ↔ label conversion. This module owns that logic so
 * doesExternalFilterPass and the picker option providers can share it.
 *
 * Data unit conventions:
 *   · API delivers buffer/floor as percent integers (10, 15, 20 …) — NOT
 *     decimals. mapRilaRow/mapVaRow store them with Number() directly so
 *     row.buffer/row.floor are already the display-ready percent number.
 *   · Picker labels use the form "N%" (e.g. "10%").
 *     Filter logic converts back to the numeric percent via parseLevelLabel.
 *   · segmentDuration is the feed's `rate_term` — a number of YEARS (1, 2, 6 …).
 *     Picker labels use "1 Year" / "N Years"; parseDurationLabel converts back.
 *     (perChangeTerm is a different field — fixed_rate_term — and is not what
 *     the Segment Duration filter reads.)
 *   · minContribution is a number (5000, 10000, …).
 */

import type { RilaRateRow, VaRateRow } from '../models/rate-row.models';

/** Row type accepted by these helpers — either RILA or VA. The other
 *  tabs (FA/IA) never call them. */
export type RilaVaRow = RilaRateRow | VaRateRow;

export type ProtectionType = 'Buffer Only' | 'Floor Only' | 'Both';
export type RateTypeKey = 'Cap' | 'Par Rate' | 'Spread' | 'Fixed' | 'Perf Trigger';

/** Derive protection type from buffer/floor presence.
 *  Returns null when neither is set (row never matches the picker). */
export function protectionTypeOf(row: RilaVaRow): ProtectionType | null {
  const hasBuf = row.buffer != null && row.buffer > 0;
  const hasFloor = row.floor != null && row.floor > 0;
  if (hasBuf && hasFloor) return 'Both';
  if (hasBuf) return 'Buffer Only';
  if (hasFloor) return 'Floor Only';
  return null;
}

/** Derive the set of rate types this row supports from which `cur*`
 *  fields are populated. Matches the legacy parent-component logic in
 *  doesExternalFilterPass — keep them in lockstep. */
export function rateTypesOf(row: RilaVaRow): Set<RateTypeKey> {
  const s = new Set<RateTypeKey>();
  // Cap < 99.99 because the data feed uses 99.99 as a sentinel for
  // "no cap"; matches existing rate-type filter logic.
  if (row.curCap != null && row.curCap > 0 && row.curCap < 99.99) s.add('Cap');
  if (row.curParRate != null && row.curParRate > 0) s.add('Par Rate');
  if (row.curSpread != null && row.curSpread > 0) s.add('Spread');
  if (row.curFixed != null && row.curFixed > 0) s.add('Fixed');
  if (row.curPerfTrigger != null && row.curPerfTrigger > 0) s.add('Perf Trigger');
  return s;
}

/** Convert a numeric percent (10, 15, 20 …) into the picker label
 *  "10%", "15%", "20%" used by buffer + floor pickers.
 *  Half-percents like 2.5 keep one decimal: "2.5%". */
export function levelLabel(value: number): string {
  // Stored values are already in percent units (post-parsePct).
  return `${value}%`;
}

/** Inverse of levelLabel — parses "10%" (or legacy "-10%") back to 10.
 *  Returns null for malformed input (defensive against stale saved-views). */
export function parseLevelLabel(label: string): number | null {
  const m = label.match(/^-?(\d+(?:\.\d+)?)%?$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  return Number.isFinite(n) ? n : null;
}

/** True when `row.buffer` matches any selected buffer level (post-parse).
 *  Empty `selected` is treated as "no filter" by the caller; this helper
 *  assumes selected.length > 0. */
export function bufferMatches(row: RilaVaRow, selectedLabels: ReadonlyArray<string>): boolean {
  if (row.buffer == null) return false;
  const target = row.buffer;
  for (const label of selectedLabels) {
    const n = parseLevelLabel(label);
    if (n != null && Math.abs(n - target) < 0.001) return true;
  }
  return false;
}

/** True when `row.floor` matches any selected floor level. */
export function floorMatches(row: RilaVaRow, selectedLabels: ReadonlyArray<string>): boolean {
  if (row.floor == null) return false;
  const target = row.floor;
  for (const label of selectedLabels) {
    const n = parseLevelLabel(label);
    if (n != null && Math.abs(n - target) < 0.001) return true;
  }
  return false;
}

/** Segment Duration picker label for a `rate_term` year count:
 *  1 → "1 Year", 6 → "6 Years", and a part-year that is a whole number of
 *  months → "6 Months" (0.5) / "3 Months" (0.25). */
export function durationLabel(years: number): string {
  if (years > 0 && years < 1) {
    const months = Math.round(years * 12 * 1000) / 1000;
    if (Number.isInteger(months)) return `${months} ${months === 1 ? 'Month' : 'Months'}`;
  }
  return `${years} ${years === 1 ? 'Year' : 'Years'}`;
}

/** Inverse of durationLabel, back to years. Also reads the older labels a
 *  saved filter may still hold ("Annual", "2 Year") so a restored selection
 *  keeps matching instead of silently filtering every row out. Returns null
 *  for anything unrecognised. */
export function parseDurationLabel(label: string): number | null {
  const s = label.trim();
  if (/^annual$/i.test(s)) return 1;
  const m = s.match(/^(\d+(?:\.\d+)?)\s*(years?|months?)?$/i);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (!Number.isFinite(n)) return null;
  return m[2] && /^month/i.test(m[2]) ? n / 12 : n;
}

/** Distinct segment durations present in `rows`, shortest first, as labels. */
export function segmentDurationOptions(rows: ReadonlyArray<{ segmentDuration?: number | null }>): string[] {
  const years = new Set<number>();
  for (const r of rows) {
    const v = r.segmentDuration;
    if (v != null && Number.isFinite(v) && v > 0) years.add(v);
  }
  return [...years].sort((a, b) => a - b).map(durationLabel);
}

/** True when `row.segmentDuration` matches any selected duration label. */
export function durationMatches(row: { segmentDuration?: number | null }, selectedLabels: ReadonlyArray<string>): boolean {
  const target = row.segmentDuration;
  if (target == null) return false;
  for (const label of selectedLabels) {
    const n = parseDurationLabel(label);
    if (n != null && Math.abs(n - target) < 0.001) return true;
  }
  return false;
}

/** True when at least one of `selectedTypes` is satisfied by this row.
 *  Empty `selectedTypes` is treated as "no filter" by the caller. */
export function rateTypeMatches(row: RilaVaRow, selectedTypes: ReadonlyArray<string>): boolean {
  const have = rateTypesOf(row);
  return selectedTypes.some((t) => have.has(t as RateTypeKey));
}
